import { useCallback, useMemo, useReducer, useRef, useState } from "react";
import { availableEvolutionIds, draftHasChanges, draftPrioritySnapshot, evolutionDraftState, evolutionPickerCounts, evolutionPickerSelectionLabel, excludeEvolution, excludeEvolutions, groupEvolutionsByCategory, includeEvolution, includeEvolutions, isDraftAtDefault, isSameSelection, mustIncludeEvolution, resetDraft, sortByDraftPriority, visibleEvolutions, } from "../evolutions/picker";
const draftReducer = (draft, action) => {
    switch (action.type) {
        case "include":
            return includeEvolution(draft, action.id);
        case "mustInclude":
            return mustIncludeEvolution(draft, action.id);
        case "exclude":
            return excludeEvolution(draft, action.id);
        case "includeAll":
            return includeEvolutions(draft, action.ids);
        case "excludeAll":
            return excludeEvolutions(draft, action.ids);
        case "reset":
            return resetDraft(action.availableIds);
    }
};
/**
 * Drives the include/exclude evolutions picker for both apps.
 *
 * The draft lives here in a `useReducer` over the pure transitions in
 * `evolutions/picker`: it is provisional until the host applies it, so a
 * dismissed picker simply discards it, and no reader of the committed
 * `includedEvolutions` ever sees a half-made selection.
 *
 * Deliberately store-agnostic — everything comes in as arguments. The two apps
 * keep the evolutions catalogue in differently-shaped slices, and this hook
 * predates any attempt to unify them.
 */
export const useEvolutionPicker = ({ evolutions, expiredEvolutions, orderedCategories, initialIncluded, initialMustInclude, selection, onSelectionChange, now, showExpired, ignoreExcludedByDefault, }) => {
    const allAvailableIds = useMemo(() => availableEvolutionIds(evolutions, ignoreExcludedByDefault), [evolutions, ignoreExcludedByDefault]);
    const initialDraft = useRef({
        included: [...initialIncluded],
        mustInclude: [...initialMustInclude],
    }).current;
    const [draft, dispatchDraft] = useReducer(draftReducer, initialDraft);
    const [searchTerm, setSearchTerm] = useState("");
    // Frozen while the user works within one selection, so a card never jumps out
    // from under their finger the moment they toggle it; recomputed when they
    // browse elsewhere or search.
    const snapshotKeyRef = useRef("");
    const snapshotRef = useRef(new Map());
    const stickyIncludedRef = useRef(new Set());
    const snapshotKey = `${JSON.stringify(selection)}|${searchTerm.trim()}`;
    if (snapshotKeyRef.current !== snapshotKey) {
        snapshotKeyRef.current = snapshotKey;
        snapshotRef.current = draftPrioritySnapshot([...evolutions, ...expiredEvolutions], draft);
        stickyIncludedRef.current = new Set(draft.included);
    }
    const visible = useMemo(() => sortByDraftPriority(visibleEvolutions({
        evolutions,
        expiredEvolutions,
        selection,
        searchTerm,
        draft,
        now,
        showExpired,
        stickyIncludedIds: stickyIncludedRef.current,
    }), snapshotRef.current), [
        evolutions,
        expiredEvolutions,
        selection,
        searchTerm,
        draft,
        now,
        showExpired,
    ]);
    const grouped = useMemo(() => groupEvolutionsByCategory(visible, orderedCategories), [visible, orderedCategories]);
    const counts = useMemo(() => evolutionPickerCounts(evolutions, expiredEvolutions, now), [evolutions, expiredEvolutions, now]);
    const expiredIds = useMemo(() => new Set(expiredEvolutions.map((evo) => evo.id)), [expiredEvolutions]);
    const selectionLabel = useMemo(() => evolutionPickerSelectionLabel(selection, orderedCategories), [selection, orderedCategories]);
    const selectSelection = useCallback((next) => {
        onSelectionChange(next);
        setSearchTerm("");
    }, [onSelectionChange]);
    return {
        /** What Apply should commit. */
        draft,
        /** The evolutions to render, already filtered and ordered. */
        visibleEvolutions: visible,
        /** The same list bucketed by category — what the search results render as. */
        groupedEvolutions: grouped,
        orderedCategories,
        counts,
        selection,
        selectionLabel,
        searchTerm,
        setSearchTerm,
        selectSelection,
        isSelectionActive: useCallback((candidate) => isSameSelection(candidate, selection), [selection]),
        stateOf: useCallback((evolutionId) => evolutionDraftState(draft, evolutionId), [draft]),
        isExpired: useCallback((evolutionId) => expiredIds.has(evolutionId), [expiredIds]),
        include: useCallback((id) => dispatchDraft({ type: "include", id }), []),
        mustInclude: useCallback((id) => dispatchDraft({ type: "mustInclude", id }), []),
        exclude: useCallback((id) => dispatchDraft({ type: "exclude", id }), []),
        includeAllVisible: useCallback(() => dispatchDraft({
            type: "includeAll",
            ids: visible.map((evo) => evo.id),
        }), [visible]),
        excludeAllVisible: useCallback(() => dispatchDraft({
            type: "excludeAll",
            ids: visible.map((evo) => evo.id),
        }), [visible]),
        reset: useCallback(() => dispatchDraft({ type: "reset", availableIds: allAvailableIds }), [allAvailableIds]),
        hasChanges: draftHasChanges(draft, initialDraft),
        isAtDefault: isDraftAtDefault(draft, allAvailableIds),
        includedCount: draft.included.length,
        totalCount: evolutions.length,
    };
};
//# sourceMappingURL=useEvolutionPicker.js.map